import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { storage } from './firebase'

// File validation constants
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif']
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif']

interface UploadValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Validate file before upload
 */
function validateFile(file: File): UploadValidationResult {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size too large. Maximum allowed: ${MAX_FILE_SIZE / 1024 / 1024}MB`
    }
  }

  // Check MIME type - allow empty MIME type if extension is valid (iOS compatibility)
  const hasValidMimeType = file.type === '' || ALLOWED_MIME_TYPES.includes(file.type)
  
  // Check file extension
  const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
  const hasValidExtension = ALLOWED_EXTENSIONS.includes(fileExtension)
  
  // Require at least one valid indicator (MIME type OR extension)
  if (!hasValidMimeType && !hasValidExtension) {
    return {
      isValid: false,
      error: `Invalid file type "${file.type}". Please select an image file (JPEG, PNG, WEBP, GIF, or HEIC).`
    }
  }
  
  if (!hasValidExtension) {
    return {
      isValid: false,
      error: `Invalid file extension "${fileExtension}". Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`
    }
  }

  // Check for potential malicious files
  if (file.name.includes('../') || file.name.includes('..\\')) {
    return {
      isValid: false,
      error: 'Invalid file name'
    }
  }

  return { isValid: true }
}

/**
 * Sanitize filename to prevent path traversal and other issues
 */
function sanitizeFileName(fileName: string): string {
  return fileName
    .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace non-alphanumeric chars except . and -
    .replace(/\.+/g, '.') // Replace multiple dots with single dot
    .replace(/^\./, '') // Remove leading dot
    .substring(0, 100) // Limit length
}

/**
 * Upload an image file to Firebase Storage with proper validation
 * @param file - The image file to upload
 * @param folder - The folder path in storage (e.g., 'chat-images/userId', 'portfolio-images/artistId')
 * @param userId - The authenticated user's ID for additional validation
 * @returns Promise<string> - The download URL of the uploaded image
 */
export async function uploadImageToStorageSecure(
  file: File, 
  folder: string, 
  userId: string
): Promise<string> {
  try {
    // Validate user authentication
    if (!userId || userId.trim().length === 0) {
      throw new Error('User authentication required for file upload')
    }

    // Validate file
    const validation = validateFile(file)
    if (!validation.isValid) {
      throw new Error(validation.error)
    }

    // Generate secure filename
    const timestamp = Date.now()
    const sanitizedName = sanitizeFileName(file.name)
    const fileName = `${timestamp}-${sanitizedName}`
    
    // Validate folder path to prevent path traversal
    if (folder.includes('../') || folder.includes('..\\')) {
      throw new Error('Invalid folder path')
    }
    
    const storagePath = `${folder}/${fileName}`
    
    // Create storage reference
    const storageRef = ref(storage, storagePath)
    
    // Upload file with metadata
    const metadata = {
      contentType: file.type,
      customMetadata: {
        uploadedBy: userId,
        uploadedAt: new Date().toISOString(),
        originalName: file.name
      }
    }
    
    const snapshot = await uploadBytes(storageRef, file, metadata)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    console.log(`File uploaded successfully: ${storagePath}`)
    return downloadURL
    
  } catch (error) {
    console.error('Error uploading image:', error)
    throw new Error(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Validate image dimensions (optional additional validation)
 */
export function validateImageDimensions(file: File): Promise<UploadValidationResult> {
  return new Promise((resolve) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    
    img.onload = () => {
      URL.revokeObjectURL(url)
      
      // Check minimum dimensions
      if (img.width < 100 || img.height < 100) {
        resolve({
          isValid: false,
          error: 'Image too small. Minimum dimensions: 100x100px'
        })
        return
      }
      
      // Check maximum dimensions
      if (img.width > 4000 || img.height > 4000) {
        resolve({
          isValid: false,
          error: 'Image too large. Maximum dimensions: 4000x4000px'
        })
        return
      }
      
      resolve({ isValid: true })
    }
    
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve({
        isValid: false,
        error: 'Invalid image file'
      })
    }
    
    img.src = url
  })
}

/**
 * Upload multiple images with validation
 */
export async function uploadMultipleImagesSecure(
  files: File[],
  folder: string,
  userId: string,
  maxFiles: number = 10
): Promise<string[]> {
  if (files.length > maxFiles) {
    throw new Error(`Too many files. Maximum allowed: ${maxFiles}`)
  }

  const uploadPromises = files.map(file => 
    uploadImageToStorageSecure(file, folder, userId)
  )

  try {
    return await Promise.all(uploadPromises)
  } catch (error) {
    console.error('Error uploading multiple images:', error)
    throw error
  }
}

/**
 * Check if user has permission to upload to a specific folder
 */
export function validateUploadPermission(folder: string, userId: string, userArtistId?: string): boolean {
  // Chat images - user can only upload to their own folder
  if (folder.startsWith('chat-images/')) {
    return folder === `chat-images/${userId}`
  }
  
  // Custom images - user can only upload to their own folder
  if (folder.startsWith('custom-images/')) {
    return folder === `custom-images/${userId}`
  }
  
  // For portfolio images
  if (folder.startsWith('portfolio-images/')) {
    return Boolean(userArtistId && folder === `portfolio-images/${userArtistId}`)
  }
  
  // For flash images
  if (folder.startsWith('flash-images/')) {
    return Boolean(userArtistId && folder === `flash-images/${userArtistId}`)
  }
  
  // Match inspiration images - user can upload to their own matches
  if (folder.startsWith('matches/')) {
    // This would need additional validation against the match document
    // For now, allow if authenticated (the storage rules will enforce ownership)
    return true
  }
  
  // Public images - authenticated users can upload
  if (folder.startsWith('public-images/')) {
    return true
  }
  
  // Searchbar uploads - user can only upload to their own folder
  if (folder.startsWith('searchbarUploads/')) {
    return folder === `searchbarUploads/${userId}`
  }
  
  return false
}
